/**
 * CanvaCallback Component
 * 
 * Handles OAuth callback from Canva after user authorization.
 * Exchanges authorization code for access tokens.
 */

import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { captureError } from "@/lib/error-tracking";
import { logger } from "@/lib/logger";
import { secureGet, secureRemove, secureClearAll } from "@/lib/secure-storage";
import { resolveRedirectUri } from "@/lib/canva/env";

export default function CanvaCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing authorization...');
  const skipAutoRedirect = searchParams.get('test_mode') === '1';

  const scheduleRedirect = (path: string, delay = 3000) => {
    if (skipAutoRedirect) {
      logger.debug('Skipping auto redirect due to test_mode flag', { path });
      return;
    }
    setTimeout(() => {
      navigate(path);
    }, delay);
  };

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // OAuth callback processing - debug logging removed
        logger.info('CanvaCallback search params', Object.fromEntries(searchParams.entries()));

        // Get authorization code and state from URL
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const testError = searchParams.get('test_error');
        const testErrorDescription = searchParams.get('test_error_description');
        const error = searchParams.get('error') || testError;
        const errorDescription = searchParams.get('error_description') || testErrorDescription;

        // Check for errors from Canva
        if (error) {
          captureError(new Error(`Canva OAuth error: ${error} - ${errorDescription}`), {
            component: 'CanvaCallback',
            action: 'handleOAuthCallback',
            additionalData: { error, errorDescription },
          });
          setStatus('error');
          setMessage(errorDescription || 'Authorization was denied or failed');
          toast.error('Failed to connect with Canva');
          
          scheduleRedirect('/buyer/catalog');
          return;
        }

        if (!code) {
          const errorDetails = {
            urlParams: Object.fromEntries(searchParams.entries()),
            fullUrl: window.location.href,
          };
          captureError(new Error('No authorization code in URL'), {
            component: 'CanvaCallback',
            action: 'handleOAuthCallback',
            additionalData: errorDetails,
          });
          setStatus('error');
          setMessage('No authorization code received. Check if redirect URI matches exactly in Canva Portal.');
          toast.error('Invalid authorization response');
          
          scheduleRedirect('/buyer/catalog');
          return;
        }

        // SECURITY: Verify state parameter from encrypted secure storage
        const storedState = await secureGet('canva_oauth_state');
        if (state !== storedState) {
          logger.warn('State mismatch - possible CSRF attack');
          secureClearAll(); // Clear all OAuth data on security failure
          setStatus('error');
          setMessage('Security validation failed');
          toast.error('Security validation failed');
          scheduleRedirect('/buyer/catalog');
          return;
        }

        // SECURITY: Get stored product context from encrypted storage
        const stateData: { productId?: string; productName?: string } = {
          productId: await secureGet('canva_oauth_productId') || undefined,
          productName: await secureGet('canva_oauth_productName') || undefined,
        };

        // Get redirect URI - must match exactly what was sent during authorization
        const { redirectUri: finalRedirectUri, source: redirectSource } = resolveRedirectUri();
        logger.debug('Resolved Canva redirect URI in callback', { redirectSource, finalRedirectUri });

        setMessage('Exchanging authorization code for tokens...');

        // SECURITY: Get code_verifier from encrypted secure storage (required for PKCE)
        const codeVerifier = await secureGet('canva_code_verifier');

        if (!codeVerifier) {
          captureError(new Error('Missing code_verifier - PKCE flow requires this'), {
            component: 'CanvaCallback',
            action: 'handleOAuthCallback',
          });
          secureClearAll(); // Clear all OAuth data
          setStatus('error');
          setMessage('Security validation failed. Please try connecting again.');
          toast.error('Security validation failed');
          scheduleRedirect('/buyer/catalog');
          return;
        }

        // Token exchange processing - debug logging removed

        // Exchange code for tokens via Supabase Edge Function
        // Get session - try multiple times in case of timing issues
        let session = null;
        let attempts = 0;
        while (!session && attempts < 3) {
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          if (currentSession) {
            session = currentSession;
            break;
          }
          attempts++;
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // SECURITY: If session is lost, try to restore from encrypted secure storage
        if (!session) {
          const storedToken = await secureGet('canva_oauth_session_token');

          if (storedToken) {
            // Token is still valid (secureGet handles expiration), use it
            session = { access_token: storedToken } as { access_token: string };
          } else {
            // Clean up all OAuth data
            secureClearAll();

            captureError(new Error('Session lost during OAuth flow'), {
              component: 'CanvaCallback',
              action: 'handleOAuthCallback',
            });
            setStatus('error');
            setMessage('Your session expired. Please log in again and try connecting to Canva.');
            toast.error('Session expired. Please log in again.');

            scheduleRedirect('/auth?redirect=/buyer/catalog');
            return;
          }
        }

        setMessage('Calling Canva API...');

        // Call edge function with explicit action in query params
        const { data, error: exchangeError } = await supabase.functions.invoke('canva-oauth', {
          body: {
            action: 'exchange', // Explicitly set action in body
            code,
            redirect_uri: finalRedirectUri,
            code_verifier: codeVerifier, // Required for PKCE token exchange
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (exchangeError) {
          captureError(exchangeError instanceof Error ? exchangeError : new Error(String(exchangeError)), {
            component: 'CanvaCallback',
            action: 'exchangeTokens',
            additionalData: {
              errorDetails: JSON.stringify(exchangeError, null, 2),
            },
          });
          
          // Check if it's an authentication error
          if (exchangeError.message?.includes('401') || exchangeError.message?.includes('Unauthorized')) {
            setStatus('error');
            setMessage('Authentication failed. Please log in again.');
            toast.error('Session expired. Please log in again.');
            scheduleRedirect('/auth?redirect=/buyer/catalog');
            return;
          }
          
          // Check for Canva-specific errors
          const errorMessage = exchangeError.message || 'Failed to exchange tokens';
          setStatus('error');
          setMessage(errorMessage);
          toast.error('Failed to connect with Canva', {
            description: errorMessage,
          });
          
          // Provide helpful error message
          if (errorMessage.includes('Invalid client') || errorMessage.includes('client_id')) {
            setMessage('Canva integration may not be published. Please check the Canva Developer Portal.');
          }
          
          scheduleRedirect('/buyer/canva-editor', 5000);
          return;
        }

        if (!data?.success) {
          captureError(new Error(`Token exchange failed: ${data?.message || 'Unknown error'}`), {
            component: 'CanvaCallback',
            action: 'exchangeTokens',
            additionalData: { responseData: data },
          });
          const errorMessage = data?.message || 'Failed to exchange tokens';
          setStatus('error');
          setMessage(errorMessage);
          toast.error('Failed to connect with Canva', {
            description: errorMessage,
          });
          
          scheduleRedirect('/buyer/canva-editor', 5000);
          return;
        }

        // SECURITY: Clean up all OAuth data from secure storage
        secureClearAll();

        setStatus('success');
        setMessage('Successfully connected to Canva!');
        toast.success('Canva account connected');

        // Redirect to editor or product page
        if (!skipAutoRedirect) {
          setTimeout(() => {
            if (stateData.productId) {
              navigate(`/buyer/catalog/${stateData.productId}?canva=connected`);
            } else {
              navigate('/buyer/canva-editor');
            }
          }, 2000);
        }

      } catch (error) {
        captureError(error instanceof Error ? error : new Error(String(error)), {
          component: 'CanvaCallback',
          action: 'handleCallback',
        });
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'An unexpected error occurred');
        toast.error('Failed to complete Canva connection');

        scheduleRedirect('/buyer/canva-editor');
      }
    };

    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4 text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="mx-auto size-12 animate-spin text-primary" />
            <h2 className="text-xl font-semibold">Connecting to Canva</h2>
            <p className="text-muted-foreground">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 className="mx-auto size-12 text-success" />
            <h2 className="text-xl font-semibold">Connected!</h2>
            <p className="text-muted-foreground">{message}</p>
            <p className="text-sm text-muted-foreground">Redirecting...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="mx-auto size-12 text-destructive" />
            <h2 className="text-xl font-semibold">Connection Failed</h2>
            <p className="text-muted-foreground">{message}</p>
            <p className="text-sm text-muted-foreground">Redirecting...</p>
          </>
        )}
      </div>
    </div>
  );
}

